import React, { useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Mail, Lock, Loader2 } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import AuthLayout from "@/components/AuthLayout";
import GoogleIcon from "@/components/GoogleIcon";
import FacebookIcon from "@/components/FacebookIcon";
import AppleIcon from "@/components/AppleIcon";
import { toast } from "@/components/ui/use-toast";
import { useI18n } from "@/lib/I18nContext";
import { safeReturnTo } from "@/lib/authReturnTo";

export default function Register() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("auth.passwords_no_match", "Passwords do not match"));
      return;
    }
    setLoading(true);
    try {
      await base44.auth.register({ email, password });
      setShowOtp(true);
    } catch (err) {
      setError(err.message || t("auth.registration_failed", "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await base44.auth.verifyOtp({ email, otpCode });
      if (result?.access_token) {
        base44.auth.setToken(result.access_token);
      }
      const dest = safeReturnTo();
      window.location.href = dest === "/" ? "/overview" : dest;
    } catch (err) {
      setError(err.message || t("auth.invalid_code", "Invalid verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    try {
      await base44.auth.resendOtp(email);
      toast({
        title: t("auth.code_sent", "Code sent"),
        description: t("auth.check_email_code", "Check your email for the new code."),
      });
    } catch (err) {
      setError(err.message || t("auth.failed_resend", "Failed to resend code"));
    }
  };

  const handleGoogle = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("google", dest === "/" ? "/overview" : dest);
  };

  const handleFacebook = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("facebook", dest === "/" ? "/overview" : dest);
  };

  const handleApple = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("apple", dest === "/" ? "/overview" : dest);
  };

  if (showOtp) {
    return (
      <AuthLayout
        icon={Mail}
        title={t("auth.verify_email", "Verify your email")}
        subtitle={`${t("auth.we_sent_code", "We sent a code to")} ${email}`}
      >
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-center mb-6">
          <InputOTP
            maxLength={6}
            value={otpCode}
            onChange={setOtpCode}
            autoFocus
            autoComplete="one-time-code"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        <Button
          className="w-full h-12 font-medium"
          onClick={handleVerify}
          disabled={loading || otpCode.length < 6}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.verifying", "Verifying...")}
            </>
          ) : (
            t("auth.verify", "Verify")
          )}
        </Button>
        <p className="text-center text-sm text-muted-foreground mt-4">
          {t("auth.didnt_receive", "Didn't receive the code?")}{" "}
          <button onClick={handleResend} className="text-primary font-medium hover:underline">
            {t("auth.resend", "Resend")}
          </button>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      icon={UserPlus}
      title={t("auth.create_your_account", "Create your account")}
      subtitle={t("auth.sign_up_to_start", "Sign up to get started")}
      footer={
        <>
          {t("auth.already_have_account", "Already have an account?")}{" "}
          <Link to="/login" className="text-primary font-medium hover:underline">
            {t("auth.log_in", "Log in")}
          </Link>
        </>
      }
    >
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-2"
        onClick={handleGoogle}
      >
        <GoogleIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_google", "Continue with Google")}
      </Button>
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-2"
        onClick={handleFacebook}
      >
        <FacebookIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_facebook", "Continue with Facebook")}
      </Button>
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-6"
        onClick={handleApple}
      >
        <AppleIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_apple", "Continue with Apple")}
      </Button>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-3 text-muted-foreground">{t("auth.or", "or")}</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email", "Email")}</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password", "Password")}</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">{t("auth.confirm_password", "Confirm Password")}</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>
        <Button type="submit" className="w-full h-12 font-medium" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.creating_account", "Creating account...")}
            </>
          ) : (
            t("auth.create_account", "Create account")
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}